// React and core libraries
import * as React from "react";
import { useContext, useMemo } from "react";

// Third-party libraries
import { Copy, Eye } from "lucide-react";
import copy from "clipboard-copy";

// Internal components
import { Button } from "../../components/ui/button";
import { Section } from "../../components/system/section";
import { Stack } from "../../components/ui/stack";
import { TypographyTable } from "../../components/ui/typography";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { Page } from "../../components/layout/page";

// Internal utilities, hooks, and contexts
import { UserContext } from "../../crud/user/context";
import {
  useTimezone,
  useToken,
  useChartColorScheme,
  ChartColorScheme,
} from "../../utils/hooks";

// Relative imports (components from same directory)
import { UsernameForm } from "./UsernameForm";
import { DefaultAccountForm } from "./DefaultAccountForm";
import { GithubKeyForm } from "./GithubKeyForm";
import { UserInfoForm } from "./UserInfoForm";
import { PasswordChangeform } from "./PasswordChangeForm";
import {
  ApiTokensSection,
  LONG_LIVED_TOKENS_ENABLED,
} from "./ApiTokensSection";

type TokenView = {
  viewing: "token" | "placeholder";
  token: string;
  placeholder: string;
};

export const Profile = (): React.ReactElement => {
  const [copyConfirmOpen, setCopyConfirmOpen] = React.useState(false);
  const [userInfoFormOpen, setUserInfoFormOpen] = React.useState(false);
  const token = useToken();
  const { user } = useContext(UserContext);
  const [tokenView, setTokenView] = React.useState<TokenView>({
    viewing: "placeholder",
    token,
    placeholder: "\u2022".repeat(token.length),
  });

  const handleCopy = async () => {
    await copy(token);
    setCopyConfirmOpen(true);
    // Auto-hide after 2 seconds
    setTimeout(() => {
      setCopyConfirmOpen(false);
    }, 2000);
  };
  const toggleViewToken = () => {
    switch (tokenView.viewing) {
      case "placeholder":
        setTokenView({
          ...tokenView,
          viewing: "token",
        });
        break;
      default:
        setTokenView({
          ...tokenView,
          viewing: "placeholder",
        });
    }
  };

  const toggleUserInfoForm = () => {
    setUserInfoFormOpen(!userInfoFormOpen);
  };

  const {
    fullName,
    preferredName,
    profile: { title, company, workEmail, phoneNumber, canContact },
    hasUsablePassword,
  } = user;

  const emailType = hasUsablePassword ? "Email" : "Work Email";

  const profileData: [string, string | boolean][] = [
    ["Full Name", fullName],
    ["Preferred Name", preferredName || ""],
    ["Job Title", title],
    ["Company", company],
    [emailType, workEmail],
    ["Phone Number", phoneNumber],
    ["Can Contact", canContact ? "Yes" : "No"],
  ];
  const breadCrumbs = useMemo(() => [{ text: "Profile" }], []);

  const [tz, setTz] = useTimezone();
  const [colorScheme, setColorScheme] = useChartColorScheme();
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzOptions = [localTz, "UTC"];
  if (!tzOptions.includes(tz)) {
    tzOptions.push(tz);
  }

  return (
    <Page title="Profile" breadCrumbs={breadCrumbs} className="max-w-2xl">
      <Stack spacing={16} className="mb-20">
        {/* TODO: update this to include an upsell moment? Seems strange that if I only have 1 workspace its essentially useless. */}
        <Section
          title="Default Workspace"
          description="This is the default workspace in which to create your clusters."
          headerButton={<DefaultAccountForm />}
        />

        <Section title="API Tokens">
          {LONG_LIVED_TOKENS_ENABLED ? (
            // Long-lived tokens are enabled, so show the token card
            <ApiTokensSection />
          ) : (
            // Long-lived tokens are disabled, so show the alternative token card
            <div className="space-y-2 border border-border rounded-lg p-4">
              <div className="flex items-center gap-1 flex-wrap">
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopy}
                      className="h-8 w-8 p-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy token to clipboard</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleViewToken}
                      className="h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View token</p>
                  </TooltipContent>
                </Tooltip>
                <pre className="bg-muted text-xs p-2 rounded flex-1 min-w-0 overflow-x-auto">
                  {tokenView[tokenView.viewing]}
                </pre>
              </div>
              {copyConfirmOpen && (
                <p className="text-sm text-green-600 mt-2">
                  Token copied to clipboard
                </p>
              )}
            </div>
          )}
        </Section>

        <Section title="Username">
          <UsernameForm />
        </Section>

        {user.hasUsablePassword && (
          <Section
            title="Password"
            headerButton={
              <Button
                onClick={() => {
                  // This will be handled by the PasswordChangeForm component
                  const changePasswordButton = document.querySelector(
                    "[data-change-password]",
                  ) as HTMLButtonElement;
                  if (changePasswordButton) changePasswordButton.click();
                }}
                size="sm"
                variant="outline"
              >
                Change Password
              </Button>
            }
          >
            <PasswordChangeform />
          </Section>
        )}

        <Section
          title="Profile Information"
          headerButton={
            <Button variant="outline" onClick={toggleUserInfoForm} size="sm">
              Edit
            </Button>
          }
        >
          <TypographyTable data={profileData} showHeaders={false} />
          <UserInfoForm
            formTitle="Edit your profile"
            handleClose={toggleUserInfoForm}
            open={userInfoFormOpen}
          />
        </Section>

        <Section
          title="GitHub Access Token"
          description={
            <>
              Add an access token to access software in private GitHub
              repositories.{" "}
              <Button
                variant="link"
                className="p-0 h-auto font-normal underline"
                asChild
              >
                <a
                  href="https://docs.coiled.io/user_guide/software_environment_creation.html#private-repositories"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </Button>
            </>
          }
        >
          <GithubKeyForm onSuccessfulSubmit={() => undefined} />
        </Section>

        <Section
          title="Display Time Zone"
          headerButton={
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tzOptions.map((opt) => (
                  <SelectItem key={`tz_${opt}`} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        >
          <div className="text-sm text-muted-foreground">
            Choose your preferred time zone for displaying dates and times
            throughout the application.
          </div>
        </Section>

        <Section
          title="Chart Color Scheme"
          headerButton={
            <Select
              value={colorScheme}
              onValueChange={(value) =>
                setColorScheme(value as ChartColorScheme)
              }
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(ChartColorScheme).map((scheme) => (
                  <SelectItem key={`colorScheme_${scheme}`} value={scheme}>
                    {scheme === ChartColorScheme.PRETTY
                      ? "Pretty"
                      : "High Contrast"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        >
          <div className="text-sm text-muted-foreground">
            Choose your preferred color scheme for charts and visualizations.
          </div>
        </Section>
      </Stack>
    </Page>
  );
};
